import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Loader2, Monitor, Smartphone, ChevronRight, ChevronDown,
  BookOpen, FileText, Video, Link as LinkIcon, HelpCircle, 
  CheckCircle, Clock, Award, Target, Users, Calendar, Bell,
  Play, Beaker, MessageSquare, Sparkles, AlertTriangle,
  Eye, Edit, ArrowLeft, Check, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const API = API_BASE;

// Content type configurations
const CONTENT_TYPES = {
  course: {
    icon: BookOpen,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    label: 'Course'
  },
  learning_lab: {
    icon: Beaker,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    label: 'Learning Lab'
  },
  campaign: {
    icon: Target,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    label: 'Campaign'
  }
};

// Status badge configurations
const STATUS_BADGES = {
  draft: { color: 'bg-zinc-600', text: 'Draft', icon: Edit },
  preview: { color: 'bg-blue-600', text: 'In Preview', icon: Eye },
  scheduled: { color: 'bg-purple-600', text: 'Scheduled', icon: Calendar },
  published: { color: 'bg-green-600', text: 'Published', icon: CheckCircle },
  active: { color: 'bg-green-600', text: 'Active', icon: CheckCircle },
  inactive: { color: 'bg-zinc-600', text: 'Inactive', icon: AlertCircle }
};

export function StatusBadge({ status }) {
  const config = STATUS_BADGES[status] || STATUS_BADGES.draft;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} text-white`}>
      <Icon className="h-3 w-3" />
      {config.text}
    </span>
  );
}

// Course Preview Component
function CoursePreview({ course, viewMode, onStartPreview }) {
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const modules = course.modules || [];
  const isAICourse = course.ai_generated || course.course_id?.startsWith('ai_course');

  return (
    <div className={`${viewMode === 'mobile' ? 'max-w-[375px] mx-auto' : ''}`}>
      {/* Hero Section */}
      <div className="relative h-64 rounded-xl overflow-hidden mb-6">
        <img
          src={course.thumbnail_url?.startsWith('/') ? `${API}${course.thumbnail_url}` : course.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'}
          alt={course.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2 mb-2">
            {isAICourse && (
              <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                AI Generated
              </span>
            )}
            <span className="bg-zinc-700 text-white text-xs px-2 py-0.5 rounded-full">
              {course.category || 'General'}
            </span>
          </div>
          <h1 className={`font-bold text-white ${viewMode === 'mobile' ? 'text-xl' : 'text-2xl'}`}>
            {course.title || 'Untitled Course'}
          </h1>
        </div>
      </div>

      {/* Course Meta */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Clock className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">{course.duration_hours || 1}h</p>
          <p className="text-xs text-zinc-500">Duration</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <BookOpen className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">{modules.length}</p>
          <p className="text-xs text-zinc-500">Modules</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Award className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">{course.cpd_points || 0}</p>
          <p className="text-xs text-zinc-500">CPD Points</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Target className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium capitalize">{course.level || 'Beginner'}</p>
          <p className="text-xs text-zinc-500">Level</p>
        </div>
      </div>

      {/* Description */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-2">About This Course</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          {course.description || 'No description provided.'}
        </p>
      </div>

      {/* Instructor */}
      {course.instructor_name && (
        <div className="mb-6 flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3">
          <div className="w-10 h-10 bg-navigate-gold/20 rounded-full flex items-center justify-center">
            <Users className="h-5 w-5 text-navigate-gold" />
          </div>
          <div>
            <p className="text-sm text-white font-medium">{course.instructor_name}</p>
            <p className="text-xs text-zinc-500">Instructor</p>
          </div>
        </div>
      )}

      {/* AI Course Sections */}
      {isAICourse && course.ai_course_data?.sections && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Course Content</h2>
          <div className="space-y-2">
            {course.ai_course_data.sections.map((section, idx) => (
              <div key={idx} className="bg-zinc-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-navigate-gold/20 rounded-full flex items-center justify-center text-xs text-navigate-gold">
                    {idx + 1}
                  </span>
                  <span className="text-white text-sm">{section.title}</span>
                </div>
                {section.subsections && (
                  <div className="ml-8 mt-2 space-y-1">
                    {section.subsections.map((sub, subIdx) => (
                      <p key={subIdx} className="text-xs text-zinc-500 flex items-center gap-1">
                        <ChevronRight className="h-3 w-3" />
                        {sub.title}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Standard Course Modules */}
      {!isAICourse && modules.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Course Modules</h2>
          <div className="space-y-2">
            {modules.map((module, idx) => (
              <div key={module.module_id || idx} className="bg-zinc-800/50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleModule(module.module_id)}
                  className="w-full flex items-center justify-between p-3 hover:bg-zinc-700/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-navigate-gold/20 rounded-full flex items-center justify-center text-xs text-navigate-gold">
                      {idx + 1}
                    </span>
                    <span className="text-white text-sm">{module.title || `Module ${idx + 1}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">{module.content?.length || 0} items</span>
                    {expandedModules[module.module_id] ? (
                      <ChevronDown className="h-4 w-4 text-zinc-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-zinc-400" />
                    )}
                  </div>
                </button>
                {expandedModules[module.module_id] && module.content && (
                  <div className="border-t border-zinc-700 p-3 space-y-2">
                    {module.content.map((item, itemIdx) => (
                      <div key={item.content_id || itemIdx} className="flex items-center gap-2 text-sm">
                        {item.type === 'video' && <Video className="h-4 w-4 text-blue-400" />}
                        {item.type === 'document' && <FileText className="h-4 w-4 text-orange-400" />}
                        {item.type === 'quiz' && <HelpCircle className="h-4 w-4 text-purple-400" />}
                        {item.type === 'embed' && <LinkIcon className="h-4 w-4 text-green-400" />}
                        <span className="text-zinc-300">{item.title || 'Untitled'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assessment Preview */}
      {(course.has_assessment || course.ai_course_data?.assessment) && (
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="h-5 w-5 text-purple-400" />
            <h3 className="text-white font-medium">NAVI Assessment</h3>
          </div>
          <p className="text-sm text-zinc-400">
            This course includes an AI-powered assessment graded by NAVI Coach.
            Learners must score 70% or higher to pass.
          </p>
        </div>
      )}

      {/* Learner Actions Preview */}
      <div className="flex gap-3">
        <Button 
          className="flex-1 bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          onClick={onStartPreview}
        >
          <Play className="h-4 w-4 mr-2" />
          Start Course
        </Button>
        <Button variant="outline" className="border-zinc-700">
          <BookOpen className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Learning Lab Preview Component
function LearningLabPreview({ lab, viewMode }) {
  return (
    <div className={`${viewMode === 'mobile' ? 'max-w-[375px] mx-auto' : ''}`}>
      {/* Hero */}
      <div className="relative h-48 rounded-xl overflow-hidden mb-6 bg-gradient-to-br from-purple-600 to-blue-600">
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Beaker className="h-16 w-16 text-white/50" />
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
            Learning Lab
          </span>
          <h1 className={`font-bold text-white mt-2 ${viewMode === 'mobile' ? 'text-xl' : 'text-2xl'}`}>
            {lab.title || 'Untitled Lab'}
          </h1>
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Target className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium capitalize">{lab.level || 'Beginner'}</p>
          <p className="text-xs text-zinc-500">Difficulty</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Sparkles className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">{lab.xp_reward || 300}</p>
          <p className="text-xs text-zinc-500">XP Reward</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <MessageSquare className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">
            {lab.scenario?.decision_points?.length || 0}
          </p>
          <p className="text-xs text-zinc-500">Decisions</p>
        </div>
      </div>

      {/* Description */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-white mb-2">Scenario Overview</h2>
        <p className="text-zinc-400 text-sm leading-relaxed">
          {lab.description || lab.scenario?.introduction || 'No description provided.'}
        </p>
      </div>

      {/* Industry & Topic */}
      <div className="flex flex-wrap gap-2 mb-6">
        {lab.industry && (
          <span className="bg-zinc-800 text-zinc-300 text-xs px-3 py-1 rounded-full">
            {lab.industry}
          </span>
        )}
        {lab.topic && (
          <span className="bg-zinc-800 text-zinc-300 text-xs px-3 py-1 rounded-full">
            {lab.topic}
          </span>
        )}
      </div>

      {/* Decision Points Preview */}
      {lab.scenario?.decision_points && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Decision Points</h2>
          <div className="space-y-3">
            {lab.scenario.decision_points.slice(0, 3).map((dp, idx) => (
              <div key={dp.id || idx} className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center text-xs text-purple-400">
                    {idx + 1}
                  </span>
                  <span className="text-white text-sm font-medium">Decision Point</span>
                </div>
                <p className="text-zinc-400 text-sm mb-3">{dp.situation?.slice(0, 150)}...</p>
                <div className="space-y-1">
                  {dp.options?.slice(0, 3).map((opt, optIdx) => (
                    <div key={optIdx} className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="w-5 h-5 border border-zinc-600 rounded flex items-center justify-center">
                        {String.fromCharCode(65 + optIdx)}
                      </span>
                      {opt.text?.slice(0, 50)}...
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {lab.scenario.decision_points.length > 3 && (
              <p className="text-center text-zinc-500 text-sm">
                +{lab.scenario.decision_points.length - 3} more decision points
              </p>
            )}
          </div>
        </div>
      )}

      {/* Start Button */}
      <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
        <Play className="h-4 w-4 mr-2" />
        Start Lab Simulation
      </Button>
    </div>
  );
}

// Campaign Preview Component
function CampaignPreview({ campaign, viewMode }) {
  return (
    <div className={`${viewMode === 'mobile' ? 'max-w-[375px] mx-auto' : ''}`}>
      {/* Hero */}
      <div className="relative h-40 rounded-xl overflow-hidden mb-6 bg-gradient-to-br from-amber-500 to-orange-600">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Target className="h-16 w-16 text-white/50" />
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <span className={`text-white text-xs px-2 py-0.5 rounded-full ${
            campaign.is_active ? 'bg-green-500' : 'bg-zinc-600'
          }`}>
            {campaign.is_active ? 'Active' : 'Inactive'}
          </span>
          <h1 className={`font-bold text-white mt-2 ${viewMode === 'mobile' ? 'text-xl' : 'text-2xl'}`}>
            {campaign.name || 'Untitled Campaign'}
          </h1>
        </div>
      </div>

      {/* Campaign Info */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
        <p className="text-zinc-300 text-sm">
          {campaign.description || 'No description provided.'}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <BookOpen className="h-5 w-5 text-zinc-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">{campaign.activities?.length || 0}</p>
          <p className="text-xs text-zinc-500">Activities</p>
        </div>
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <Sparkles className="h-5 w-5 text-amber-400 mx-auto mb-1" />
          <p className="text-sm text-white font-medium">+{campaign.bonus_xp || 0} XP</p>
          <p className="text-xs text-zinc-500">Completion Bonus</p>
        </div>
      </div>

      {/* Badge Preview */}
      <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2">
          <Award className="h-4 w-4 text-amber-400" />
          Badge Reward
        </h3>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
            <Award className="h-6 w-6 text-amber-400" />
          </div>
          <div>
            <p className="text-white font-medium">
              {campaign.badge_name || `${campaign.campaign_type?.replace(/_/g, ' ')} Champion`}
            </p>
            <p className="text-xs text-zinc-500">Earned on completion</p>
          </div>
        </div>
      </div>

      {/* Activities */}
      {campaign.activities && campaign.activities.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white mb-3">Campaign Activities</h2>
          <div className="space-y-2">
            {campaign.activities.map((activity, idx) => (
              <div key={activity.activity_id || idx} className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3">
                <span className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center text-xs text-zinc-400">
                  {idx + 1}
                </span>
                {activity.activity_type === 'course' ? (
                  <BookOpen className="h-4 w-4 text-blue-400" />
                ) : (
                  <Beaker className="h-4 w-4 text-purple-400" />
                )}
                <div className="flex-1">
                  <p className="text-white text-sm">{activity.title}</p>
                  <p className="text-xs text-zinc-500 capitalize">{activity.activity_type?.replace(/_/g, ' ')}</p>
                </div>
                <CheckCircle className="h-4 w-4 text-zinc-600" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Learner View */}
      <div className="border-t border-zinc-800 pt-4">
        <p className="text-xs text-zinc-500 mb-3">Learner will see:</p>
        <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black">
          <Target className="h-4 w-4 mr-2" />
          Join Campaign
        </Button>
      </div>
    </div>
  );
}

// Main Preview Modal Component
export default function ContentPreviewModal({
  open,
  onClose,
  contentType, // 'course' | 'learning_lab' | 'campaign'
  content,
  onPublish,
  onSaveDraft,
  onStartPreview,  // New prop to handle starting the course preview
  token
}) {
  const [viewMode, setViewMode] = useState('desktop');
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const typeConfig = CONTENT_TYPES[contentType] || CONTENT_TYPES.course;
  const TypeIcon = typeConfig.icon;

  // Determine current status
  const getStatus = () => {
    if (contentType === 'campaign') {
      return content.is_active ? 'active' : 'inactive';
    }
    if (content.status === 'published' || content.is_published) return 'published';
    return content.status || 'draft';
  };

  const currentStatus = getStatus();
  const isPublished = currentStatus === 'published' || currentStatus === 'active';

  const handlePublish = async () => {
    setPublishing(true);
    try {
      if (onPublish) {
        await onPublish();
      }
      toast.success(`${typeConfig.label} published successfully!`);
      onClose();
    } catch (err) {
      console.error('Publish failed:', err);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (onSaveDraft) {
        await onSaveDraft();
      }
      toast.success('Saved as draft');
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Validation warnings
  const getWarnings = () => {
    const warnings = [];
    
    if (contentType === 'course') {
      if (!content.title) warnings.push('Course title is missing');
      if (!content.description) warnings.push('Course description is missing');
      if (!content.thumbnail_url) warnings.push('Course thumbnail is missing');
      if (!content.modules?.length && !content.ai_course_data?.sections?.length) {
        warnings.push('Course has no content modules');
      }
    }
    
    if (contentType === 'learning_lab') {
      if (!content.title) warnings.push('Lab title is missing');
      if (!content.scenario?.decision_points?.length) warnings.push('Lab has no decision points');
    }
    
    if (contentType === 'campaign') {
      if (!content.name) warnings.push('Campaign name is missing');
      if (!content.activities?.length) warnings.push('Campaign has no activities');
    }
    
    return warnings;
  };

  const warnings = getWarnings();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[95vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${typeConfig.bgColor} rounded-lg flex items-center justify-center`}>
              <TypeIcon className={`h-5 w-5 ${typeConfig.color}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <DialogTitle className="text-lg font-semibold text-white">Preview: {typeConfig.label}</DialogTitle>
                <StatusBadge status={currentStatus} />
              </div>
              <p className="text-sm text-zinc-500">
                Review how this will appear to learners before publishing
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('desktop')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'desktop' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              title="Desktop View"
            >
              <Monitor className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'mobile' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              title="Mobile View"
            >
              <Smartphone className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="mx-4 mt-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 text-amber-400 mb-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium text-sm">Review Required</span>
            </div>
            <ul className="space-y-1">
              {warnings.map((warning, idx) => (
                <li key={idx} className="text-sm text-amber-300/80 flex items-center gap-2">
                  <span className="w-1 h-1 bg-amber-400 rounded-full" />
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-zinc-950">
          <div className={`${viewMode === 'mobile' ? 'bg-zinc-900 rounded-3xl p-4 mx-auto shadow-2xl border border-zinc-800' : ''}`}
               style={viewMode === 'mobile' ? { maxWidth: '400px' } : {}}>
            {contentType === 'course' && <CoursePreview course={content} viewMode={viewMode} onStartPreview={onStartPreview} />}
            {contentType === 'learning_lab' && <LearningLabPreview lab={content} viewMode={viewMode} />}
            {contentType === 'campaign' && <CampaignPreview campaign={content} viewMode={viewMode} />}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-4 border-t border-zinc-800 bg-zinc-900">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-zinc-400"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Editor
          </Button>
          
          <div className="flex items-center gap-3">
            {!isPublished && onSaveDraft && (
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saving}
                className="border-zinc-700"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
                Save as Draft
              </Button>
            )}
            
            {onPublish && <Button
              onClick={handlePublish}
              disabled={publishing || warnings.length > 0}
              className={`${
                warnings.length > 0 
                  ? 'bg-zinc-700 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              {isPublished ? 'Update & Publish' : 'Publish Now'}
            </Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export individual components for reuse
export { CoursePreview, LearningLabPreview, CampaignPreview };
